import { useMemo } from "react"
import { Table, Tag } from "antd"
import type { ColumnsType } from "antd/es/table"
import type { Key } from "react"
import { Role, RoleTableProps, RoleStatus } from "../types"
import { RoleStatusLabels, RoleStatusColors } from "../constants"
import { TableActionColumn } from "@/components/TableActionColumn"
import { ID } from "@/types/common"
import dayjs from "dayjs"

const RoleTable = ({
  roles,
  onEdit,
  onDelete,
  loading = false,
  selectedIds = [],
  onSelectionChange,
  pagination,
  onPageChange,
}: RoleTableProps) => {
  const columns: ColumnsType<Role> = useMemo(
    () => [
      {
        title: "角色名称",
        dataIndex: "name",
        key: "name",
        ellipsis: true,
      },
      {
        title: "角色代码",
        dataIndex: "code",
        key: "code",
        ellipsis: true,
      },
      {
        title: "状态",
        dataIndex: "status",
        key: "status",
        width: 100,
        render: (status: RoleStatus) => {
          const roleStatus =
            status === RoleStatus.ACTIVE ? RoleStatus.ACTIVE : RoleStatus.INACTIVE
          return (
            <Tag color={RoleStatusColors[roleStatus] || "default"}>
              {RoleStatusLabels[roleStatus]}
            </Tag>
          )
        },
      },
      {
        title: "备注",
        dataIndex: "note",
        key: "note",
        ellipsis: true,
      },
      {
        title: "创建时间",
        dataIndex: "createdAt",
        key: "createdAt",
        ellipsis: true,
      },
      TableActionColumn<Role>({
        onEdit,
        onDelete: (record: Role | ID) => {
          const id =
            typeof record === "object" && record !== null
              ? (record as Role).id
              : record
          onDelete(id)
        },
        getId: (record) => record.id,
        deleteConfirmDescription: (record) =>
          `确定要删除角色 "${record.name}" 吗？`,
        width: 200,
      }) as any,
    ],
    [onEdit, onDelete],
  )

  const rowSelection = onSelectionChange
    ? {
        selectedRowKeys: selectedIds,
        onChange: (selectedRowKeys: Key[]) => {
          onSelectionChange(selectedRowKeys as ID[])
        },
        getCheckboxProps: (record: Role) => ({
          name: record.name,
        }),
      }
    : undefined

  return (
    <Table
      columns={columns}
      dataSource={roles}
      rowKey="id"
      loading={loading}
      rowSelection={rowSelection}
      pagination={{
        current: pagination?.page,
        pageSize: pagination?.pageSize,
        total: pagination?.total,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) =>
          `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
        pageSizeOptions: ["10", "20", "50", "100"],
        onChange: (page, pageSize) => {
          onPageChange?.(page, pageSize)
        },
      }}
      size="middle"
      showSorterTooltip={false}
    />
  )
}

export default RoleTable
